import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Plus, Trash2, GripVertical, Save, ChevronDown, ChevronUp, Wand2, Loader2, Check, X } from 'lucide-react';
import { useAISuggest } from '../hooks/useRubrics';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Drag and drop imports
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const CATEGORIES = [
  'Pragmatics',
  'Language',
  'Expressive Language',
  'Receptive Language',
  'Narrative Language',
  'Articulation',
  'Fluency',
  'Voice',
  'AAC/Functional Communication',
  'Classroom Participation',
  'Other'
];

// Helper to truncate goal text to create domain label
const truncateGoal = (text, maxLength = 50) => {
  if (!text) return '';
  const trimmed = text.trim();
  if (trimmed.length <= maxLength) return trimmed;
  return trimmed.substring(0, maxLength).trim() + '...';
};

// Sortable Domain Item Component
const SortableDomainItem = ({ 
  domain, 
  domainIndex, 
  isExpanded, 
  levels,
  onUpdateDomain, 
  onUpdateDescriptor,
  onToggleExpanded, 
  onRemove 
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: domain.id || `domain-${domainIndex}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style}
      className={`border rounded-xl p-4 bg-white ${isDragging ? 'shadow-lg ring-2 ring-primary/20' : ''}`}
    >
      <div className="flex items-start gap-3">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="touch-none cursor-grab active:cursor-grabbing p-1 -m-1 rounded hover:bg-muted transition-colors mt-2"
          data-testid={`drag-handle-${domainIndex}`}
        >
          <GripVertical className="w-4 h-4 text-muted-foreground" />
        </button>
        <Textarea
          data-testid={`domain-name-${domainIndex}`}
          value={domain.domain_name}
          onChange={(e) => onUpdateDomain(domainIndex, 'domain_name', e.target.value)}
          placeholder="Domain name"
          className="flex-1 rounded-xl min-h-[40px] resize-y"
          rows={1}
        />
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onToggleExpanded(domainIndex)}
          className="text-muted-foreground"
        >
          {isExpanded ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onRemove(domainIndex)}
          data-testid={`remove-domain-${domainIndex}`}
          className="text-destructive hover:text-destructive"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
      
      {isExpanded && (
        <div className="mt-4 pl-7 space-y-3">
          <p className="text-sm text-muted-foreground">
            Optional: Add specific descriptors for this domain at each level
          </p>
          {levels.map((level, levelIndex) => (
            <div key={levelIndex} className="grid grid-cols-12 gap-2 items-start">
              <div className="col-span-2 text-sm text-muted-foreground pt-2">
                {level.level_label}:
              </div>
              <div className="col-span-10">
                <Textarea
                  data-testid={`domain-${domainIndex}-level-${levelIndex}-descriptor`}
                  value={domain.level_descriptors?.[levelIndex] || ''}
                  onChange={(e) => onUpdateDescriptor(domainIndex, levelIndex, e.target.value)}
                  placeholder={`Descriptor for ${level.level_label}`}
                  className="rounded-xl min-h-[60px] text-sm"
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Drag Overlay Component (shown while dragging)
const DragOverlayContent = ({ domain }) => {
  if (!domain) return null;
  
  return (
    <div className="border rounded-xl p-4 bg-white shadow-xl ring-2 ring-primary/30">
      <div className="flex items-center gap-3">
        <GripVertical className="w-4 h-4 text-primary" />
        <span className="font-medium">{domain.domain_name || 'Unnamed Domain'}</span>
      </div>
    </div>
  );
};

export const RubricBuilder = ({ rubric, onSave, onCancel }) => {
  const { suggestRubric, loading: aiLoading } = useAISuggest();
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [numberOfLevels, setNumberOfLevels] = useState(4);
  const [domains, setDomains] = useState([]);
  const [levels, setLevels] = useState([]);
  const [expandedDomains, setExpandedDomains] = useState({});
  const [activeDragId, setActiveDragId] = useState(null);
  
  // Goals state
  const [goals, setGoals] = useState([]);
  const [domainSource, setDomainSource] = useState('goals_as_domains'); // 'goals_as_domains' or 'ai_generated'
  const [selectedGoalForAI, setSelectedGoalForAI] = useState(null);
  
  // AI Suggestion state
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [showSuggestionPreview, setShowSuggestionPreview] = useState(false);

  // Configure drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Reset form when rubric prop changes
  useEffect(() => {
    if (rubric) {
      setTitle(rubric.title || '');
      setCategory(rubric.category || '');
      setNumberOfLevels(rubric.number_of_levels || 4);
      setDomainSource(rubric.domain_source || 'goals_as_domains');
      
      // Load goals
      if (rubric.goals && rubric.goals.length > 0) {
        setGoals(rubric.goals.map(g => ({ ...g })));
      } else if (rubric.goal_area) {
        // Backward compat: convert single goal_area to goals array
        setGoals([{
          id: `goal-${Date.now()}`,
          text: rubric.goal_area,
          domain_label: truncateGoal(rubric.goal_area)
        }]);
      } else {
        setGoals([]);
      }
      
      const domainsWithIds = (rubric.domains || []).map((d, i) => ({
        ...d,
        id: d.id || `domain-${Date.now()}-${i}`
      }));
      setDomains(domainsWithIds);
      setLevels(rubric.levels || []);
    } else {
      setTitle('');
      setCategory('');
      setNumberOfLevels(4);
      setDomainSource('goals_as_domains');
      setGoals([]);
      setDomains([]);
      setLevels([]);
    }
    setExpandedDomains({});
    setSelectedGoalForAI(null);
  }, [rubric]);

  // Sync goals to domains when in "goals_as_domains" mode
  const syncGoalsToDomains = useCallback(() => {
    if (domainSource === 'goals_as_domains') {
      const newDomains = goals.map((goal, i) => ({
        id: `domain-goal-${goal.id}`,
        domain_name: goal.domain_label || truncateGoal(goal.text),
        display_order: i,
        level_descriptors: []
      }));
      setDomains(newDomains);
    }
  }, [goals, domainSource]);

  // Goal management
  const addGoal = () => {
    const newGoal = {
      id: `goal-${Date.now()}`,
      text: '',
      domain_label: ''
    };
    setGoals([...goals, newGoal]);
  };

  const updateGoal = (index, field, value) => {
    const updated = [...goals];
    updated[index] = { ...updated[index], [field]: value };
    
    // Auto-update domain_label when text changes (if label is empty or was auto-generated)
    if (field === 'text' && (!updated[index].domain_label || updated[index].domain_label === truncateGoal(goals[index].text))) {
      updated[index].domain_label = truncateGoal(value);
    }
    
    setGoals(updated);
  };

  const removeGoal = (index) => {
    const updated = goals.filter((_, i) => i !== index);
    setGoals(updated);
    
    // If we removed the selected goal for AI, clear selection
    if (selectedGoalForAI === goals[index].id) {
      setSelectedGoalForAI(updated.length > 0 ? updated[0].id : null);
    }
  };

  // Domain management
  const addDomain = () => {
    const newDomain = {
      id: `domain-${Date.now()}`,
      domain_name: '',
      display_order: domains.length,
      level_descriptors: levels.map(() => '')
    };
    setDomains([...domains, newDomain]);
  };

  const removeDomain = (index) => {
    setDomains(domains.filter((_, i) => i !== index));
    const newExpanded = { ...expandedDomains };
    delete newExpanded[index];
    setExpandedDomains(newExpanded);
  };

  const updateDomain = (index, field, value) => {
    const updated = [...domains];
    updated[index] = { ...updated[index], [field]: value };
    setDomains(updated);
  };

  const updateDomainDescriptor = (domainIndex, levelIndex, value) => {
    const updated = [...domains];
    if (!updated[domainIndex].level_descriptors) {
      updated[domainIndex].level_descriptors = levels.map(() => '');
    }
    updated[domainIndex].level_descriptors[levelIndex] = value;
    setDomains(updated);
  };

  const updateLevel = (index, field, value) => {
    const updated = [...levels];
    updated[index] = { ...updated[index], [field]: value };
    setLevels(updated);
  };

  const handleLevelCountChange = (newCount) => {
    const count = parseInt(newCount);
    setNumberOfLevels(count);
    
    if (count > levels.length) {
      const newLevels = [...levels];
      for (let i = levels.length; i < count; i++) {
        newLevels.push({
          level_value: i + 1,
          level_label: `Level ${i + 1}`,
          level_description: ''
        });
      }
      setLevels(newLevels);
    } else if (count < levels.length) {
      setLevels(levels.slice(0, count));
    }
  };

  const toggleDomainExpanded = (index) => {
    setExpandedDomains(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Domain source change handler
  const handleDomainSourceChange = (value) => {
    setDomainSource(value);
    
    if (value === 'goals_as_domains') {
      // Sync goals to domains immediately
      syncGoalsToDomains();
    }
    // If switching to AI, user will need to click "Generate Domains"
  };

  // State for generating levels
  const [generatingLevels, setGeneratingLevels] = useState(false);

  // Apply goals as domains button - also generates level labels/descriptions
  const handleApplyGoalsAsDomains = async () => {
    console.log('handleApplyGoalsAsDomains called');
    console.log('Goals:', goals);
    console.log('Current levels:', levels);
    console.log('numberOfLevels:', numberOfLevels);
    
    syncGoalsToDomains();
    
    // Check if levels are still default/blank
    const levelsAreDefault = levels.every(level => 
      level.level_label.match(/^Level \d+$/) && !level.level_description
    );
    
    console.log('levelsAreDefault:', levelsAreDefault);
    
    if (levelsAreDefault && goals.length > 0) {
      // Generate level labels and descriptions from goals
      setGeneratingLevels(true);
      toast.info('Generating level descriptions...');
      
      try {
        const goalTexts = goals.map(g => g.text).filter(t => t && t.trim().length > 0);
        // Also pass domain names for domain-specific descriptors
        const domainNames = goals.map(g => g.text).filter(t => t && t.trim().length > 0);
        
        console.log('goalTexts for API:', goalTexts);
        console.log('domainNames for API:', domainNames);
        
        if (goalTexts.length > 0) {
          const token = localStorage.getItem('token');
          console.log('Token present:', !!token);
          
          const response = await axios.post(`${API}/generate-levels`, {
            goals: goalTexts,
            num_levels: numberOfLevels,
            category: category || null,
            domains: domainNames
          }, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          console.log('API response:', response.data);
          
          if (response.data && response.data.levels) {
            // Set the level labels and descriptions
            setLevels(response.data.levels.map((l, i) => ({
              level_value: l.level_value || i + 1,
              level_label: l.level_label || `Level ${i + 1}`,
              level_description: l.level_description || ''
            })));
            
            // If we have domain-specific descriptors, apply them to domains
            if (response.data.domain_level_descriptors && response.data.domain_level_descriptors.length > 0) {
              // Update domains with level_descriptors
              setDomains(prevDomains => {
                return prevDomains.map((domain, idx) => {
                  // Find matching domain descriptor by index or name
                  const domainDescriptor = response.data.domain_level_descriptors[idx] || 
                    response.data.domain_level_descriptors.find(d => 
                      domain.domain_name.toLowerCase().includes(d.domain_name?.toLowerCase()?.substring(0, 20))
                    );
                  
                  if (domainDescriptor && domainDescriptor.descriptors) {
                    return {
                      ...domain,
                      level_descriptors: domainDescriptor.descriptors
                    };
                  }
                  return domain;
                });
              });
              toast.success('Goals applied as domains with AI-generated level descriptors!');
            } else {
              toast.success('Goals applied as domains & level descriptions generated!');
            }
          } else {
            toast.success('Goals applied as domains');
          }
        } else {
          toast.success('Goals applied as domains (no valid goal text)');
        }
      } catch (err) {
        console.error('Failed to generate levels:', err);
        console.error('Error details:', err.response?.data);
        toast.error('Failed to generate level descriptions. Goals applied as domains.');
      } finally {
        setGeneratingLevels(false);
      }
    } else {
      console.log('Skipping level generation - levelsAreDefault:', levelsAreDefault, 'goals.length:', goals.length);
      toast.success('Goals applied as domains');
    }
  };

  // Generate level descriptions from existing domains (for manual mode or any time)
  const handleGenerateLevelsFromDomains = async () => {
    if (domains.length === 0) {
      toast.error('Add at least one domain/criteria first');
      return;
    }

    setGeneratingLevels(true);
    toast.info('Generating level descriptions from domains...');

    try {
      const domainNames = domains.map(d => d.domain_name).filter(n => n && n.trim().length > 0);
      const goalTexts = goals.map(g => g.text).filter(t => t && t.trim().length > 0);
      
      // Use goals if available, otherwise create a generic goal from domains
      const goalsForApi = goalTexts.length > 0 
        ? goalTexts 
        : [`Student will demonstrate skills in: ${domainNames.join(', ')}`];

      const token = localStorage.getItem('token');
      
      const response = await axios.post(`${API}/generate-levels`, {
        goals: goalsForApi,
        num_levels: numberOfLevels,
        category: category || null,
        domains: domainNames
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data && response.data.levels) {
        // Set the level labels and descriptions
        setLevels(response.data.levels.map((l, i) => ({
          level_value: l.level_value || i + 1,
          level_label: l.level_label || `Level ${i + 1}`,
          level_description: l.level_description || ''
        })));

        // If we have domain-specific descriptors, apply them to domains
        if (response.data.domain_level_descriptors && response.data.domain_level_descriptors.length > 0) {
          setDomains(prevDomains => {
            return prevDomains.map((domain, idx) => {
              const domainDescriptor = response.data.domain_level_descriptors[idx] || 
                response.data.domain_level_descriptors.find(d => 
                  domain.domain_name.toLowerCase().includes(d.domain_name?.toLowerCase()?.substring(0, 20))
                );
              
              if (domainDescriptor && domainDescriptor.descriptors) {
                return {
                  ...domain,
                  level_descriptors: domainDescriptor.descriptors
                };
              }
              return domain;
            });
          });
          toast.success('Level descriptions generated with domain-specific descriptors!');
        } else {
          toast.success('Level descriptions generated!');
        }
      }
    } catch (err) {
      console.error('Failed to generate levels from domains:', err);
      toast.error('Failed to generate level descriptions');
    } finally {
      setGeneratingLevels(false);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (event) => {
    setActiveDragId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveDragId(null);

    if (over && active.id !== over.id) {
      setDomains((items) => {
        const oldIndex = items.findIndex(item => (item.id || `domain-${items.indexOf(item)}`) === active.id);
        const newIndex = items.findIndex(item => (item.id || `domain-${items.indexOf(item)}`) === over.id);
        
        const reordered = arrayMove(items, oldIndex, newIndex);
        return reordered.map((item, i) => ({ ...item, display_order: i }));
      });

      setExpandedDomains((prev) => {
        const oldIndex = domains.findIndex(item => (item.id || `domain-${domains.indexOf(item)}`) === active.id);
        const newIndex = domains.findIndex(item => (item.id || `domain-${domains.indexOf(item)}`) === over.id);
        
        if (oldIndex === -1 || newIndex === -1) return prev;
        
        const newExpanded = {};
        Object.keys(prev).forEach(key => {
          const idx = parseInt(key);
          if (idx === oldIndex) {
            newExpanded[newIndex] = prev[key];
          } else if (oldIndex < newIndex) {
            if (idx > oldIndex && idx <= newIndex) {
              newExpanded[idx - 1] = prev[key];
            } else {
              newExpanded[idx] = prev[key];
            }
          } else {
            if (idx >= newIndex && idx < oldIndex) {
              newExpanded[idx + 1] = prev[key];
            } else {
              newExpanded[idx] = prev[key];
            }
          }
        });
        return newExpanded;
      });
    }
  };

  const handleDragCancel = () => {
    setActiveDragId(null);
  };

  const getActiveDomain = () => {
    if (!activeDragId) return null;
    return domains.find(d => (d.id || `domain-${domains.indexOf(d)}`) === activeDragId);
  };

  // AI Suggestion handler
  const handleSuggestRubric = async () => {
    // Get the goal to use for AI
    let goalText = '';
    if (domainSource === 'ai_generated') {
      const selectedGoal = goals.find(g => g.id === selectedGoalForAI);
      goalText = selectedGoal?.text || '';
    }
    
    if (!goalText.trim() || goalText.trim().length < 10) {
      toast.error('Please select a goal with at least 10 characters');
      return;
    }
    
    try {
      const suggestion = await suggestRubric(goalText, category || null, numberOfLevels);
      setAiSuggestion(suggestion);
      setShowSuggestionPreview(true);
    } catch (err) {
      toast.error('Failed to generate suggestion. Please try again.');
    }
  };

  const applySuggestion = () => {
    if (!aiSuggestion) return;
    
    // Apply title if empty
    if (!title && aiSuggestion.suggested_title) {
      setTitle(aiSuggestion.suggested_title);
    }
    
    // Apply category if empty
    if (!category && aiSuggestion.suggested_category && CATEGORIES.includes(aiSuggestion.suggested_category)) {
      setCategory(aiSuggestion.suggested_category);
    }
    
    // REPLACE domains entirely - now with level_descriptors from AI
    const domainsWithIds = (aiSuggestion.domains || []).map((d, i) => ({
      id: `domain-${Date.now()}-${i}`,
      domain_name: d.domain_name,
      display_order: d.display_order || i,
      level_descriptors: d.level_descriptors || []  // Include AI-generated level descriptors
    }));
    setDomains(domainsWithIds);
    
    // REPLACE levels
    const aiLevels = aiSuggestion.levels || [];
    const newLevels = [];
    for (let i = 0; i < numberOfLevels; i++) {
      if (aiLevels[i]) {
        newLevels.push({
          level_value: i + 1,
          level_label: aiLevels[i].level_label,
          level_description: aiLevels[i].level_description || ''
        });
      } else {
        newLevels.push({
          level_value: i + 1,
          level_label: `Level ${i + 1}`,
          level_description: ''
        });
      }
    }
    setLevels(newLevels);
    
    setShowSuggestionPreview(false);
    setExpandedDomains({});
    toast.success('AI suggestion applied! You can now customize the rubric.');
  };

  const dismissSuggestion = () => {
    setShowSuggestionPreview(false);
    setAiSuggestion(null);
  };

  const handleSave = () => {
    if (!title.trim()) {
      toast.error('Please enter a rubric title');
      return;
    }
    if (!category) {
      toast.error('Please select a category');
      return;
    }
    if (goals.length === 0) {
      toast.error('Please add at least one goal');
      return;
    }
    if (goals.some(g => !g.text.trim())) {
      toast.error('Please fill in all goal texts');
      return;
    }
    if (domains.length === 0) {
      toast.error('Please add at least one domain');
      return;
    }

    const cleanDomains = domains.map((d, i) => ({
      domain_name: d.domain_name,
      display_order: i,
      level_descriptors: d.level_descriptors
    }));

    // Build goal_area for backward compat (join all goals)
    const goalAreaText = goals.map(g => g.text).join('\n\n');

    onSave({
      title,
      category,
      goals: goals.map(g => ({ id: g.id, text: g.text, domain_label: g.domain_label })),
      goal_area: goalAreaText,
      domain_source: domainSource,
      number_of_levels: numberOfLevels,
      domains: cleanDomains,
      levels
    });
  };

  const domainIds = domains.map((d, i) => d.id || `domain-${i}`);

  return (
    <div className="space-y-6">
      {/* Basic Info */}
      <Card className="soft-card">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg">Rubric Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="title">Rubric Title *</Label>
              <Input
                id="title"
                data-testid="rubric-title-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Pragmatic Language Skills"
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="category">Category *</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="category-select" className="rounded-xl">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="levels">Number of Levels</Label>
            <Select value={numberOfLevels.toString()} onValueChange={handleLevelCountChange}>
              <SelectTrigger data-testid="levels-select" className="rounded-xl w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[3, 4, 5, 6].map(n => (
                  <SelectItem key={n} value={n.toString()}>{n}-Point</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Goals Section */}
      <Card className="soft-card">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Goals *</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Add the therapy goals you want to track
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={addGoal}
              data-testid="add-goal-btn"
              className="rounded-xl"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Goal
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {goals.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-xl">
              <p>No goals added yet.</p>
              <p className="text-sm mt-1">Click "Add Goal" to add your first goal.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {goals.map((goal, index) => (
                <div key={goal.id} className="border rounded-xl p-4 bg-white">
                  <div className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium text-primary mt-2">
                      {index + 1}
                    </span>
                    <div className="flex-1 space-y-3">
                      <Textarea
                        data-testid={`goal-text-${index}`}
                        value={goal.text}
                        onChange={(e) => updateGoal(index, 'text', e.target.value)}
                        placeholder="Enter the full goal text..."
                        className="rounded-xl min-h-[80px] resize-y"
                      />
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Domain Label (editable)</Label>
                        <Input
                          data-testid={`goal-domain-label-${index}`}
                          value={goal.domain_label}
                          onChange={(e) => updateGoal(index, 'domain_label', e.target.value)}
                          placeholder="Short label for the domain row"
                          className="rounded-xl text-sm"
                        />
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeGoal(index)}
                      data-testid={`remove-goal-${index}`}
                      className="text-destructive hover:text-destructive flex-shrink-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Domain/Criteria Source Selection */}
      <Card className="soft-card">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg">Domain/Criteria Source</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <RadioGroup value={domainSource} onValueChange={handleDomainSourceChange}>
            <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors">
              <RadioGroupItem value="goals_as_domains" id="goals_as_domains" className="mt-1" />
              <div className="flex-1">
                <Label htmlFor="goals_as_domains" className="font-medium cursor-pointer">
                  Use Goals as Domains/Criteria
                </Label>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Each goal becomes a domain/criteria row in the rubric. Labels are editable above.
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors">
              <RadioGroupItem value="ai_generated" id="ai_generated" className="mt-1" />
              <div className="flex-1">
                <Label htmlFor="ai_generated" className="font-medium cursor-pointer">
                  Generate Domains/Criteria from a Single Goal
                </Label>
                <p className="text-sm text-muted-foreground mt-0.5">
                  AI will generate multiple domain/criteria objectives from one selected goal.
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors">
              <RadioGroupItem value="manual" id="manual" className="mt-1" />
              <div className="flex-1">
                <Label htmlFor="manual" className="font-medium cursor-pointer">
                  Add My Own Domains/Criteria
                </Label>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Manually create custom domains/criteria for your rubric.
                </p>
              </div>
            </div>
          </RadioGroup>

          {domainSource === 'goals_as_domains' && goals.length > 0 && (
            <div className="pt-2">
              <Button
                variant="outline"
                onClick={handleApplyGoalsAsDomains}
                className="rounded-xl"
                disabled={generatingLevels}
                data-testid="apply-goals-as-domains-btn"
              >
                {generatingLevels ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating Levels...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Apply Goals as Domains/Criteria
                  </>
                )}
              </Button>
            </div>
          )}

          {domainSource === 'ai_generated' && goals.length > 0 && (
            <div className="pt-2 space-y-3">
              <div className="space-y-2">
                <Label>Select Goal for AI Generation</Label>
                <Select value={selectedGoalForAI || ''} onValueChange={setSelectedGoalForAI}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Select a goal" />
                  </SelectTrigger>
                  <SelectContent>
                    {goals.map((goal, index) => (
                      <SelectItem key={goal.id} value={goal.id}>
                        Goal {index + 1}: {truncateGoal(goal.text, 40)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="outline"
                onClick={handleSuggestRubric}
                disabled={aiLoading || !selectedGoalForAI}
                className="rounded-xl"
                data-testid="generate-domains-btn"
              >
                {aiLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4 mr-2" />
                    Generate Domains/Criteria from Goal
                  </>
                )}
              </Button>
            </div>
          )}

          {domainSource === 'manual' && (
            <div className="pt-2">
              <p className="text-sm text-muted-foreground mb-3">
                Add your own domains/criteria using the "Add Domain/Criteria" button in the Domains section below.
                Once you've added domains, click "Generate from Domains" in the Level Labels section below to auto-generate level descriptions.
              </p>
              <Button
                variant="outline"
                onClick={addDomain}
                className="rounded-xl"
                data-testid="quick-add-domain-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add First Domain/Criteria
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Domains/Criteria with Drag and Drop */}
      <Card className="soft-card">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Domains/Criteria</CardTitle>
              {domains.length > 1 && (
                <p className="text-xs text-muted-foreground mt-1">
                  Drag the handle to reorder domains/criteria
                </p>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={addDomain}
              data-testid="add-domain-btn"
              className="rounded-xl"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Domain/Criteria
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {domains.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-xl">
              <p>No domains/criteria yet.</p>
              <p className="text-sm mt-1">
                {domainSource === 'goals_as_domains' 
                  ? 'Click "Apply Goals as Domains" above, or add domains/criteria manually.'
                  : domainSource === 'manual'
                  ? 'Click "Add Domain/Criteria" above to add your first domain.'
                  : 'Click "Generate Domains from Goal" above, or add domains/criteria manually.'}
              </p>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
            >
              <SortableContext items={domainIds} strategy={verticalListSortingStrategy}>
                <div className="space-y-3">
                  {domains.map((domain, domainIndex) => (
                    <SortableDomainItem
                      key={domain.id || `domain-${domainIndex}`}
                      domain={domain}
                      domainIndex={domainIndex}
                      isExpanded={expandedDomains[domainIndex]}
                      levels={levels}
                      onUpdateDomain={updateDomain}
                      onUpdateDescriptor={updateDomainDescriptor}
                      onToggleExpanded={toggleDomainExpanded}
                      onRemove={removeDomain}
                    />
                  ))}
                </div>
              </SortableContext>
              <DragOverlay>
                <DragOverlayContent domain={getActiveDomain()} />
              </DragOverlay>
            </DndContext>
          )}
        </CardContent>
      </Card>

      {/* Level Labels */}
      <Card className="soft-card">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Level Labels & Descriptions</CardTitle>
            {domains.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleGenerateLevelsFromDomains}
                disabled={generatingLevels}
                className="rounded-xl"
                data-testid="generate-levels-from-domains-btn"
              >
                {generatingLevels ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4 mr-2" />
                    Generate from Domains
                  </>
                )}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {levels.map((level) => (
              <div key={level.level_value} className="grid grid-cols-12 gap-3 items-start">
                <div className="col-span-1 flex items-center justify-center h-10 w-10 rounded-lg bg-muted text-sm font-medium">
                  {level.level_value}
                </div>
                <div className="col-span-3">
                  <Input
                    data-testid={`level-label-${level.level_value}`}
                    value={level.level_label}
                    onChange={(e) => updateLevel(level.level_value - 1, 'level_label', e.target.value)}
                    placeholder="Label"
                    className="rounded-xl"
                  />
                </div>
                <div className="col-span-8">
                  <Textarea
                    data-testid={`level-description-${level.level_value}`}
                    value={level.level_description}
                    onChange={(e) => updateLevel(level.level_value - 1, 'level_description', e.target.value)}
                    placeholder="Description (optional)"
                    className="rounded-xl min-h-[60px] resize-y"
                    rows={2}
                  />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-4">
        {onCancel && (
          <Button
            variant="outline"
            onClick={onCancel}
            data-testid="cancel-btn"
            className="rounded-xl"
          >
            Cancel
          </Button>
        )}
        <Button
          onClick={handleSave}
          data-testid="save-rubric-btn"
          className="rounded-xl btn-interaction"
        >
          <Save className="w-4 h-4 mr-2" />
          Save Rubric
        </Button>
      </div>

      {/* AI Suggestion Preview Dialog */}
      <Dialog open={showSuggestionPreview} onOpenChange={setShowSuggestionPreview}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-primary" />
              <DialogTitle>AI Suggestion Preview</DialogTitle>
            </div>
          </DialogHeader>
          
          {aiSuggestion && (
            <div className="flex-1 overflow-y-auto space-y-4 py-4">
              {aiSuggestion.suggested_title && (
                <div>
                  <Label className="text-xs text-muted-foreground">Suggested Title</Label>
                  <p className="font-medium">{aiSuggestion.suggested_title}</p>
                </div>
              )}
              
              {aiSuggestion.suggested_category && (
                <div>
                  <Label className="text-xs text-muted-foreground">Suggested Category</Label>
                  <p className="text-sm">{aiSuggestion.suggested_category}</p>
                </div>
              )}
              
              <div>
                <Label className="text-xs text-muted-foreground">Generated Domains/Criteria ({aiSuggestion.domains?.length || 0})</Label>
                <ul className="mt-1 space-y-1">
                  {aiSuggestion.domains?.map((d, i) => (
                    <li key={i} className="text-sm flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center text-xs text-primary">
                        {i + 1}
                      </span>
                      {d.domain_name}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div>
                <Label className="text-xs text-muted-foreground">Levels ({aiSuggestion.levels?.length || 0})</Label>
                <div className="mt-1 space-y-2">
                  {aiSuggestion.levels?.map((l, i) => (
                    <div key={i} className="text-sm p-2 bg-muted/50 rounded-lg">
                      <span className="font-medium">{l.level_label}</span>
                      {l.level_description && (
                        <p className="text-xs text-muted-foreground mt-0.5">{l.level_description}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              {aiSuggestion.scoring_notes && (
                <div>
                  <Label className="text-xs text-muted-foreground">Notes</Label>
                  <p className="text-sm text-muted-foreground">{aiSuggestion.scoring_notes}</p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={dismissSuggestion} data-testid="dismiss-suggestion-btn">
              <X className="w-4 h-4 mr-2" />
              Dismiss
            </Button>
            <Button onClick={applySuggestion} data-testid="apply-suggestion-btn">
              <Check className="w-4 h-4 mr-2" />
              Apply Suggestion
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RubricBuilder;
